import {
  Dispatch,
  SetStateAction,
  createContext,
  ReactElement,
  ReactNode,
  useState,
  useMemo,
  useContext,
} from "react";
import {
  CONTRACT,
  ESIGN,
  ORDERS,
  USER_MANAGEMENT,
  DOCUMENTS,
} from "util/featureFlags";

export type FeatureFlag =
  | string
  | ESIGN
  | ORDERS
  | USER_MANAGEMENT
  | CONTRACT
  | DOCUMENTS;
type Flags = Record<FeatureFlag, boolean>;

type FeatureFlagContextValue = {
  flags: Flags;
  setFlags: Dispatch<SetStateAction<Flags>>;
  hasFlag(flag: FeatureFlag): boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  setFlags: () => {
    return;
  },
  hasFlag: () => false,
});

let envFlags: Flags = {};

// Load default flags from env
try {
  envFlags = Object.fromEntries(
    process.env.REACT_APP_FEATURE_FLAGS?.split(",")?.map((flag) => {
      const [flagName, value]: (string | undefined)[] = flag.split(":");
      return [
        flagName ?? "",
        value?.toLocaleLowerCase() === "true" || value === "1",
      ];
    }) ?? []
  );
} catch (e) {
  // eslint-disable-next-line no-console
  console.debug("Env not defined, feature flags unset.", (e as Error).message);
}

const FeatureFlagProvider = ({
  children,
  initialFlags = envFlags,
}: {
  children: ReactNode;
  initialFlags?: Flags;
}): ReactElement => {
  const [flags, setFlags] = useState<Flags>(initialFlags);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      flags,
      setFlags,
      hasFlag: (flag: FeatureFlag) => flags[flag],
    }),
    [flags]
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlagProvider;

export const useFeatureFlags = (): FeatureFlagContextValue => {
  const flagContext = useContext(FeatureFlagContext);
  if (!flagContext) {
    throw Error("Feature flag provider not found");
  }
  return flagContext;
};
